'use server'

import {useCallback, useEffect, useMemo, useState} from 'react'
import useStorage from './useStorage'
import {v4 as uuidv4} from 'uuid'
import queryString from 'querystring'
import {jwtDecode, JwtPayload} from "jwt-decode"
import getPkce from "oauth-pkce"
import {AuthService, AuthState} from "../index"
import {bytesToHex} from "../../crypto"
import {prepareDeviceArgs} from "../../wasm/ops"
import {useDeviceContext} from "./useDevice";
import {clearAllData} from "../../crypto/indexeddb.secure";
import {isSameOrigin} from '../../auth/accountFlowParams'

const desiredAccessLifetimeSeconds = 60 * 60 * 24
export const ExceededMaxLoginDurationErrorText = "ExceededMaxLoginDuration"

export const tokenStorageKey = "practicemd-token-storage"
export const pkceSessionKey = "practicemd-pkce"

const get = require('lodash/get')

export type UserInfo = {
    email: string
    givenName: string
    familyName: string
    name: string
}

export type CallbackFn = (location: { pathname?: string, search?: string, hash?: string }) => Promise<void>;

interface LoginFnArgsObject {
    fromUri: string;
    email?: string;
    doLogin?: boolean
    loginPrompt?: string
}

function isLoginFnArgsObject(obj: any): obj is LoginFnArgsObject {
    return typeof obj === 'object' &&
        typeof obj.fromUri === 'string' &&
        (typeof obj.email === 'string' || typeof obj.email === 'undefined') &&
        (typeof obj.doLogin === 'boolean' || typeof obj.doLogin === 'undefined') &&
        (typeof obj.loginPrompt === 'string' || typeof obj.loginPrompt === 'undefined')
}

export type LoginFn = (args: LoginFnArgsObject|string|any) => void
export type LogoutFn = (postLogoutUri?: string) => void;

const scope = `openid profile email phone`

export default function useAuth(): { authService: AuthService, authState: AuthState } {

    const device = useDeviceContext()

    const [tokenStorage, setTokenStorage] = useStorage(undefined, tokenStorageKey)

    const [authState, setAuthState] = useState<AuthState>(determineAuthState(tokenStorage))


    useEffect(() => {
        setAuthState(determineAuthState(tokenStorage))

        let expiresIn = tokenStorage?.expiresAt ? tokenStorage?.expiresAt - new Date().getTime() : 0
        if (expiresIn <= 0) {
            setTokenStorage(null)
        }
        const timeoutId = setTimeout(() => {
            setTokenStorage(null)
        }, expiresIn)
        return () => clearTimeout(timeoutId)
    }, [tokenStorage, setTokenStorage])


    const [pkce, setPkce] = useState<Pkce>()
    const client_id = useMemo(() => process.env.GATSBY_PRACTICEMD_IDP_CLIENT_ID as string, [])
    const endpoint = useMemo(() => process.env.GATSBY_PRACTICEMD_IDP_ISSUER_URI, [])
    const [redirectUri, setRedirectUri] = useState<string>()
    useEffect(() => {
        setRedirectUri(`${document.location.origin}/callback/`)
    }, [])

    const loginFn = useCallback<LoginFn>((args) => {
        console.log('[useAuth] loginFn called with args:', args);
        let fromUri : string
        let email : string|undefined
        let doLogin: boolean|undefined
        let loginPrompt : string|undefined

        if(isLoginFnArgsObject(args)) {
            fromUri = args.fromUri
            console.log('[useAuth] Using fromUri from args:', fromUri);
            email = args.email
            doLogin = args.doLogin
            loginPrompt = args.loginPrompt
        } else {
            if (typeof document !== 'undefined') {
                fromUri = document?.location?.href
            } else {
                console.error(`unexpected state`)
                fromUri = ""
            }
            if(typeof args==='string') {
                loginPrompt = args
            }
        }

        if (authState?.isAuthenticated || authState?.isPending) {
            console.log('[useAuth] Bailing early - isAuthenticated:', authState?.isAuthenticated, 'isPending:', authState?.isPending);
            return
        }

        // Use redirectUri from state if available, otherwise compute directly
        // (needed because redirectUri is set via useEffect after first render)
        const effectiveRedirectUri = redirectUri || `${document.location.origin}/callback/`

        console.log('[useAuth] Final fromUri for OAuth state:', fromUri);
        let params = {
            response_type: "code",
            client_id: client_id,
            redirect_uri: effectiveRedirectUri,
            state: encodeRequestState(fromUri),
            scope: scope,
            code_challenge_method: "S256",
            code_challenge: pkce?.code_challenge,
        }

        if (loginPrompt) {
            (params as any).login_prompt = loginPrompt
        }

        if (typeof (email) !== 'undefined' && !!email) {
            (params as any).email = email
        }

        if (typeof (doLogin) !== 'undefined' && !!doLogin) {
            (params as any).doLogin = doLogin
        }

        const paramStr = Object.entries(params).map(e => `${e[0]}=${e[1]}`).join("&")
        // Include fromUri as direct query param (authorize page expects this)
        const fromUriParam = `fromUri=${encodeURIComponent(fromUri)}`
        let url = `${process.env.GATSBY_PRACTICEMD_APP_URL}/authorize/?${fromUriParam}&${paramStr}`

        setAuthState({
                accessToken: null,
                idToken: null,
                isPending: true,
                isAuthenticated: false,
                userinfo: emptyUserinfo(),
                registeredUser: false,
            }
        )

        console.log('[useAuth] Redirecting to authorize URL:', url);
        window.location.assign(url)

        // let a = document.createElement('a')
        // a.href = url
        // a.target = "_self"
        //
        // document.body.appendChild(a)
        // a.click()

    }, [pkce, client_id, redirectUri, authState])

    const logoutFn = useCallback<LogoutFn>((postLogoutUri) => {
        console.log('[logout] Starting logout...')
        // Clear OAuth token storage
        setTokenStorage(null)
        try {
            localStorage.removeItem('practicemd-token-storage')
            localStorage.removeItem('practicemd-pkce')
            sessionStorage.removeItem('practicemd-token-storage')
            sessionStorage.removeItem('practicemd-pkce')
            console.log('[logout] Cleared OAuth storage')
        } catch (e) {
            console.warn('[logout] Failed to clear localStorage:', e)
        }

        const redirectTo = postLogoutUri || `${document.location.origin}/logout/`

        // Clear BOTH IndexedDB databases, then redirect
        // 1. practicemd - device material, wrap keys, keyrings
        // 2. PracticeMD_SecureKeyring - secure keyring with CryptoKey objects
        // Clear the legacy SecureKeyring DB via raw deleteDatabase —
        // useAuth doesn't depend on src/crypto/secureStorage since the
        // only thing it ever needed was this tear-down, which is one
        // call. Keeps logout covering legacy-user migration artifacts
        // without dragging the secureStorage module back in.
        const deleteLegacySecureKeyringDB = () => new Promise<void>((resolve) => {
            try {
                const req = indexedDB.deleteDatabase('PracticeMD_SecureKeyring')
                req.onsuccess = () => resolve()
                req.onerror = () => resolve()
                req.onblocked = () => resolve()
            } catch {
                resolve()
            }
        })

        Promise.all([
            clearAllData().catch(e => {
                console.warn('[logout] Failed to clear practicemd database:', e)
            }),
            deleteLegacySecureKeyringDB(),
        ]).then(() => {
            console.log('[logout] Cleared all device data, redirecting...')
            document.location.assign(redirectTo)
        })
    }, [setTokenStorage])


    const callbackFn = useCallback<CallbackFn>(async (location) => {
        location = location === null ? {pathname: "", search: "", hash: ""} : location
        if (location?.search?.startsWith("?")) {
            location.search = location.search.slice(1)
        }
        const values = queryString.parse(location?.search ?? "")
        // console.log("\n\n")
        // console.log("==========================")
        // console.log("location [callback]")
        // console.log("==========================")
        // console.log(JSON.stringify(location, null, " "))
        // console.log("--------------------------")
        // console.log(JSON.stringify(values, null, " "))
        // console.log("==========================")
        // console.log("\n\n")
        // console.log(values.state)
        let decodedRequestState

        try {
            decodedRequestState = decodeRequestState(values.state)
            if (decodedRequestState && decodedRequestState.access_token) {
                const o = decodedRequestState
                console.log('[useAuth] Extracting tokens from state (email login second pass):', {
                    hasAccessToken: !!o.access_token,
                    hasRegisteredUser: !!o.registeredUser,
                    registeredUser: o.registeredUser
                })
                const accessToken = o.access_token
                const idToken = o.id_token
                const expiresAt = o.expiresAt
                const registeredUser = o.registeredUser

                // console.log("todo: [looks like we authenticated via email]")
                let tokens = {
                    accessToken,
                    idToken,
                    expiresAt,
                    registeredUser,
                    userinfo: registeredUser,
                }

                if (tokens && tokens.registeredUser) {
                    setTokenStorage(tokens)
                } else {
                    if (tokens && !tokens.registeredUser) {
                        const err = new Error("User not found");
                        (err as any).type = "UserNotFoundError";
                        (err as any).message = "Please use an alternate login method"
                        throw err
                    }
                    const err = new Error("Unexpected error");
                    (err as any).type = "UnexpectedError";
                    (err as any).message = "Login Failed"
                    throw err
                }
                return
            }
        } catch (e) {
        }

        const otpToken = values.otp_token
        let tokens = null
        if (typeof (otpToken) === 'string' && typeof (otpToken) !== "undefined" && otpToken != null && otpToken.trim().length > 0) {
            var email
            try {
                email = decodeRequestState(values.state).email
            } catch (e) {
                email = null
            }

            // This browser's device receives the recovery key: the server
            // encrypts it to the enc kid we send, and provisionWithAuthorizer
            // below binds the same device to the account. A browser with no
            // device gets the SILENT one first, as every flow that binds a
            // browser does (Appendix C, C3); the protect step and the kit
            // follow on the callback page (pages/callback.tsx). A locked
            // device is unlocked, as for any device op.
            const {record: wasmDevice} = await prepareDeviceArgs({allowSilent: true})
            const deviceName = `Email Device (${wasmDevice.sigKid.substring(0, 8)})`

            const response = await fetch(process.env.GATSBY_PRACTICEMD_API_ENDPOINT as string, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: `
                        mutation {
                        completeEmailAuthentication(
                         email: ${JSON.stringify(email)}
                         challenge_answer: "opaque"
                         session: "${otpToken}"
                         client_id: "${values.client_id}"
                         clientEncKid: "${wasmDevice.encKid}"
                         clientSigKid: "${wasmDevice.sigKid}"
                        ){
                           error
                           access_token
                           id_token
                           session
                           registered_user
                           refresh_token
                           recovery_seed_encrypted
                         }
                        }
                    `,
            })

            const json = await response.json()
            const error = get(json, 'data.completeEmailAuthentication.error', get(json, 'error', null))
            if (error && typeof (error) === 'string' && error === ExceededMaxLoginDurationErrorText) {
                const err = new Error(error);
                (err as any).type = error;
                (err as any).message = "Link has expired. Please login again"
                throw err
            }
            if (!error) {
                const accessToken = get(json, 'data.completeEmailAuthentication.access_token')
                const idToken = get(json, 'data.completeEmailAuthentication.id_token')
                const refreshToken = get(json, 'data.completeEmailAuthentication.refresh_token')
                const expiresAt = new Date().getTime() + desiredAccessLifetimeSeconds * 1000
                const registeredUser = get(json, 'data.completeEmailAuthentication.registered_user')
                console.log('[useAuth] completeEmailAuthentication response:', {
                    email_from_state: email,
                    registeredUser: registeredUser,
                    full_response: json?.data?.completeEmailAuthentication
                })
                const recoverySeedEncrypted = get(json, 'data.completeEmailAuthentication.recovery_seed_encrypted')
                if (!recoverySeedEncrypted) {
                    throw new Error('Missing recovery seed from email authentication')
                }

                try {
                    const recoveryDeviceSeed = await device.decryptX25519Envelope(recoverySeedEncrypted)
                    const provisionResult = await device.provisionWithAuthorizer(
                        bytesToHex(recoveryDeviceSeed),
                        deviceName,
                        'regular',
                    )
                    if (!provisionResult.success) {
                        throw new Error(provisionResult.error || 'Provisioning via recovery device failed')
                    }
                    // ENG-53/ENG-55: no eager bulk keyring pull; the device is
                    // functional via the chain-walk bootstrap and recovers older
                    // keys on first use via pull-on-miss.
                } catch (syncErr) {
                    console.error('[EmailAuth] Recovery-based provisioning failed:', syncErr)
                    throw syncErr
                }

                tokens = {
                    access_token: accessToken,
                    id_token: idToken,
                    refresh_token: refreshToken,
                    expiresAt,
                    registeredUser,
                    userinfo: registeredUser,
                }
                // embed oauth tokens in an updated state for handling in the callback
                const updatedState = addToState(values.state, tokens)
                let params = {
                    state: updatedState,
                }
                const paramStr = Object.entries(params).map(e => `${e[0]}=${e[1]}`).join('&')
                let redirectUri = values.redirect_uri
                if (typeof (redirectUri) === 'string' && redirectUri.endsWith("/")) {
                    redirectUri = redirectUri.substring(0, redirectUri.length - 1)
                }
                // Same origin only, else our own callback. redirect_uri came
                // back in the emailed link exactly as the sign-in page's query
                // had it, so whoever wrote that query chose it, and the tokens
                // ride in `state` below (2026-10-15 review; the API refuses an
                // off-site one too, in InitiateEmailAuthentication). The
                // "starts with http" check stays for the broken links
                // ("undefined", a relative path) of the race that existed
                // before useDeviceContext was added to useAuth.
                if (typeof redirectUri !== 'string' || !redirectUri.startsWith('http') || !isSameOrigin(redirectUri)) {
                    redirectUri = `${document.location.origin}/callback`
                }
                const url1 = `${redirectUri}/?${paramStr}`
                // console.log("todo: the url is " + url1)
                document.location.assign(url1)
                return
            }
        } else {
            const parsedStateObj = decodeRequestState(values.state)
            const authReqRandomState = parsedStateObj.state
            let params = {
                grant_type: 'authorization_code',
                client_id: client_id,
                scope: scope,
                redirect_uri: redirectUri,
                state: authReqRandomState,
                code: values.code,
                code_verifier: pkce?.code_verifier,
            }
            const paramStr = Object.entries(params).map(e => `${e[0]}=${e[1]}`).join('&')
            let url = `${endpoint}/token`

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: paramStr,
            })

            const json = await response.json()
            const {access_token = null, id_token = null, expires_in = null} = json == null ? {} : json
            const expiresAt = determineAccessExpiresAt(expires_in)
            const registeredUser = await getRegisteredUserinfo(access_token)
            tokens = {
                accessToken: access_token,
                idToken: id_token,
                expiresAt: expiresAt,
                registeredUser: registeredUser,
                userinfo: registeredUser,
            }
            // console.log(`tokens from IDP: ${JSON.stringify(tokens, null, " ")}`)
        }
        if (tokens && tokens.registeredUser) {
            // console.log(`setting token storage to ${JSON.stringify(tokens, null, " ")}`)
            setTokenStorage(tokens)
        } else {
            if (tokens && !tokens.registeredUser) {
                const err = new Error("User not found");
                (err as any).type = "UserNotFoundError";
                (err as any).message = "Please use an alternate login method"
                throw err
            }
            const err = new Error("Unexpected error");
            (err as any).type = "UnexpectedError";
            (err as any).message = "Login Failed"
            throw err
        }
    }, [client_id, endpoint, pkce, setTokenStorage, redirectUri])

    useEffect(() => {
        getPkce(50, (error, {verifier, challenge}) => {
            setPkce({
                code_challenge: challenge,
                code_verifier: verifier,
            })
        })
    }, [])

    return {
        authState,
        authService: {
            login: loginFn,
            logout: logoutFn,
            handleCallback: callbackFn,
        }
    }
}

const decodeRequestState = function (state: string | string[] | undefined) {
    if (typeof (state) !== 'string') {
        return {}
    }

    try {
        return JSON.parse(atob(state))
    } catch (e) {
        console.log(e)
        return {}
    }
}
const encodeRequestState = (fromUri: string) => btoa(JSON.stringify({fromUri: fromUri, uuid: uuidv4()}))

export const extractFromUriFromState = (state: string) => decodeRequestState(state).fromUri
export const addToState = (state: string | string[] | undefined, obj: {}) => {
    const decoded = decodeRequestState(state)
    return btoa(JSON.stringify({...decoded, ...obj}))
}

function emptyUserinfo(): UserInfo {
    return {
        name: "",
        givenName: "",
        familyName: "",
        email: "",
    }
}

let extractUserinfo = function (idToken?: string) {
    if (idToken === null || idToken === undefined || (typeof idToken !== "string")) {
        return null
    }
    let decoded: JwtPayload = jwtDecode(idToken)
    decoded = decoded == null ? {} : decoded
    return {
        email: (decoded as any).email,
        familyName: (decoded as any).family_name,
        givenName: (decoded as any).given_name,
        name: (decoded as any).name
    }
}
let determineAuthState: (tokenStorage?: {
    expiresAt?: number,
    accessToken?: string,
    idToken?: string,
    registeredUser?: boolean
}) => AuthState = function (tokenStorage) {
    const expiresAt = tokenStorage?.expiresAt
    const access_token = tokenStorage?.accessToken
    const id_token = tokenStorage?.idToken
    const registeredUser = tokenStorage?.registeredUser

    const expiresIn = expiresAt ? expiresAt - new Date().getTime() : 0
    const isAuthenticated = expiresIn > 0 && access_token !== null && access_token !== undefined && id_token !== null && id_token !== undefined
    const userinfo = !!registeredUser ? registeredUser : extractUserinfo(id_token)
    return {
        accessToken: access_token,
        idToken: id_token,
        isPending: false,
        isAuthenticated: isAuthenticated,
        registeredUser: isAuthenticated ? registeredUser : null,
        userinfo: userinfo,
    } as AuthState
}
let determineAccessExpiresAt = function (expires_in: number) {
    let tokenExpiresAt = typeof (expires_in) === "number" ? new Date().getTime() + (expires_in * 1000) : 0
    let desiredExpiresAt = new Date().getTime() + desiredAccessLifetimeSeconds * 1000
    return Math.min(desiredExpiresAt, tokenExpiresAt)
}

async function getRegisteredUserinfo(accessToken?: string) {

    const response = await fetch(process.env.GATSBY_PRACTICEMD_API_ENDPOINT as string, {
        method: 'POST',
        headers: {
            'Content-type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
        },
        body: `
{
  userinfo {
    error {
      type
      message
      properties
    }
    user
  }
}

`,
    })
    const json = await response.json()
    let err = get(json, 'data.userinfo.error', null)
    if (err) {
        throw err
    }
    return get(json, 'data.userinfo.user', null)

}

interface Pkce {
    code_challenge: string
    code_verifier: string
}
